import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { Api } from '../../api/api';
import { Button, Paper, WithSpinner } from '../../components';
import { errorMessage } from '../../utils';
import { actions } from '../../state';
import styles from './admins.module.scss';
import {
  formatActivityRecordDate,
  formatActivityRecordTarget,
  formatActivityRecordType,
  formatAdminDisplayName,
  getActivityRecordAdminPreview,
  getActivityRecordDetailsLines,
  getActivityRecordId,
  getActivityRecordStorageKey,
} from './admins.helpers';

const getLocationPayload = location => {
  if (!location || !location.state || typeof location.state !== 'object') {
    return {};
  }

  return location.state;
};

const getStoredPayload = (adminId, recordId) => {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }

  try {
    const storedPayload = sessionStorage.getItem(
      getActivityRecordStorageKey(adminId, recordId)
    );

    return storedPayload ? JSON.parse(storedPayload) : {};
  } catch (e) {
    return {};
  }
};

const AdminRecord = ({ dispatch, history, location, match: { params } }) => {
  const adminId = params.id;
  const recordId = params.recordId;
  const routePayload = getLocationPayload(location);
  const storedPayload = getStoredPayload(adminId, recordId);

  const initialAdmin =
    routePayload.admin && String(routePayload.admin.id) === String(adminId)
      ? routePayload.admin
      : storedPayload.admin &&
        String(storedPayload.admin.id) === String(adminId)
      ? storedPayload.admin
      : null;

  const initialRecord =
    routePayload.record &&
    String(getActivityRecordId(routePayload.record)) === String(recordId)
      ? routePayload.record
      : storedPayload.record &&
        String(getActivityRecordId(storedPayload.record)) === String(recordId)
      ? storedPayload.record
      : null;

  const [admin, setAdmin] = useState(initialAdmin);
  const [record, setRecord] = useState(initialRecord);
  const [loading, setLoading] = useState(!initialAdmin || !initialRecord);

  useEffect(() => {
    let isMounted = true;

    const loadRecord = async () => {
      if (admin && record) {
        return;
      }

      setLoading(true);

      try {
        const recordResponse = record
          ? null
          : await Api.getAdminActivityRecord(recordId);

        if (!isMounted) {
          return;
        }

        const matchedRecord = record || recordResponse || null;

        setAdmin(
          admin ||
            (matchedRecord
              ? getActivityRecordAdminPreview(matchedRecord)
              : null)
        );
        setRecord(matchedRecord);
      } catch (e) {
        if (isMounted) {
          dispatch(actions.error.setErrorAction(errorMessage(e)));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRecord();

    return () => {
      isMounted = false;
    };
  }, [admin, adminId, dispatch, record, recordId]);

  useEffect(() => {
    if (!admin || !record) {
      return;
    }

    if (typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      sessionStorage.setItem(
        getActivityRecordStorageKey(adminId, recordId),
        JSON.stringify({ admin, record })
      );
    } catch (e) {
      // Ignore storage failures after the detail page is loaded.
    }
  }, [admin, adminId, record, recordId]);

  const title = `Vartotojas #${
    admin && admin.id ? admin.id : adminId
  } - Įrašas`;
  const changeLines = useMemo(
    () => (record ? getActivityRecordDetailsLines(record) : ['-']),
    [record]
  );
  const userLabel = admin
    ? `${formatAdminDisplayName(admin)}${admin.id ? ` (#${admin.id})` : ''}`
    : record && record.admin_id
    ? `Vartotojas #${record.admin_id}`
    : `Vartotojas #${adminId}`;

  const renderRow = (label, value) => (
    <div className={styles.recordRow} key={label}>
      <div className={styles.recordRowLabel}>{label}</div>
      <div className={styles.recordRowValue}>{value}</div>
    </div>
  );

  return (
    <section className={styles.recordPage}>
      <h1 className={styles.recordTitle}>{title}</h1>
      <Paper size="lg">
        <WithSpinner loading={loading}>
          <div className={styles.recordCard}>
            {record ? (
              <div className={styles.recordDetails}>
                {renderRow('Data ir laikas', formatActivityRecordDate(record))}
                {renderRow('Tipas', formatActivityRecordType(record))}
                {renderRow('Tikslas', formatActivityRecordTarget(record))}
                {renderRow('Vartotojas', userLabel)}
                {renderRow(
                  'Pokyčiai',
                  <div className={styles.recordChanges}>
                    {changeLines.map((line, index) => (
                      <p
                        className={styles.recordChangeLine}
                        key={`${line}-${index}`}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.recordEmpty}>Įrašas nerastas.</div>
            )}
            <div className={styles.recordActions}>
              <Button
                color="outline"
                type="button"
                classes={{ button: styles.recordBackButton }}
                onClick={() => history.goBack()}
              >
                Atgal
              </Button>
            </div>
          </div>
        </WithSpinner>
      </Paper>
    </section>
  );
};

AdminRecord.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.object,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
      recordId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default connect()(AdminRecord);
